// src/views/Dashboard/DashboardAdSpend.js
import React, { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

import { API_URI } from "../../helpers"; // ajustá si en el admin es otra ruta

const n = (v) => (typeof v === "number" && !Number.isNaN(v) ? v : 0);
const fmtUsd = (v) => `$${n(v).toFixed(2)}`;
const fmtNum = (v) => `${n(v).toFixed(2)}`;

// Hook para detectar mobile
const useIsMobile = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false
  );

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [breakpoint]);

  return isMobile;
};

// Helpers para fechas
const formatDate = (date) => date.toISOString().split("T")[0];
const getDateRange = (preset) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const to = formatDate(today);

  switch (preset) {
    case "today": {
      return { from: to, to };
    }
    case "yesterday": {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      return { from: formatDate(yesterday), to: formatDate(yesterday) };
    }
    case "7d": {
      const from = new Date(today);
      from.setDate(from.getDate() - 6);
      return { from: formatDate(from), to };
    }
    case "14d": {
      const from = new Date(today);
      from.setDate(from.getDate() - 13);
      return { from: formatDate(from), to };
    }
    case "30d": {
      const from = new Date(today);
      from.setDate(from.getDate() - 29);
      return { from: formatDate(from), to };
    }
    case "mtd": {
      const from = new Date(today.getFullYear(), today.getMonth(), 1);
      return { from: formatDate(from), to };
    }
    default:
      return { from: undefined, to: undefined };
  }
};

const DATE_PRESETS = [
  { key: "today", label: "Hoy" },
  { key: "yesterday", label: "Ayer" },
  { key: "7d", label: "7 días" },
  { key: "14d", label: "14 días" },
  { key: "30d", label: "30 días" },
  { key: "mtd", label: "Este mes" },
  { key: "custom", label: "Personalizado" },
];

const DashboardAdSpendPage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const isMobile = useIsMobile();

  // Preset por defecto: hoy
  const [activePreset, setActivePreset] = useState("today");
  const defaultRange = getDateRange("today");
  const [from, setFrom] = useState(defaultRange.from);
  const [to, setTo] = useState(defaultRange.to);

  const [selectedDate, setSelectedDate] = useState(null);
  
  // Key para forzar refresh del SalesTimeline
  const [refreshKey, setRefreshKey] = useState(0);

  // ---- Fetch ----
  const fetchData = async (override) => {
    try {
      setLoading(true);
      setError(null);

      const params = {};
      const fromValue =
        override && Object.prototype.hasOwnProperty.call(override, "from")
          ? override.from
          : from;
      const toValue =
        override && Object.prototype.hasOwnProperty.call(override, "to")
          ? override.to
          : to;

      if (fromValue) params.from = fromValue;
      if (toValue) params.to = toValue;

      const query = new URLSearchParams(params).toString();
      const url = `${API_URI}/dashboard/ad-spend${query ? `?${query}` : ""}`;

      console.log("DashboardAdSpend → GET", url);

      const res = await fetch(url, { method: "GET" });

      if (!res.ok) {
        const text = await res.text();
        console.error("Error DashboardAdSpend", res.status, text);
        throw new Error(text || "Error al cargar datos");
      }

      const json = await res.json();
      setData(json || []);
    } catch (err) {
      console.error(err);
      setError(err && err.message ? err.message : "Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  // Cargar con últimos 7 días por defecto
  useEffect(() => {
    fetchData({ from: defaultRange.from, to: defaultRange.to });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Helpers ----
  const handlePresetClick = (preset) => {
    setActivePreset(preset);
    if (preset !== "custom") {
      const range = getDateRange(preset);
      setFrom(range.from);
      setTo(range.to);
      setSelectedDate(null);
      setRefreshKey((k) => k + 1); // Forzar refresh del SalesTimeline
      fetchData(range);
    }
  };

  const handleApplyCustomRange = () => {
    setActivePreset("custom");
    setSelectedDate(null);
    setRefreshKey((k) => k + 1); // Forzar refresh del SalesTimeline
    fetchData({ from, to });
  };

  const handleRefresh = () => {
    setRefreshKey((k) => k + 1); // Forzar refresh del SalesTimeline
    fetchData({ from, to });
  };

  const handlePointClick = (date) => {
    setSelectedDate(date);
  };

  const selectedDayData = useMemo(
    () => data.find((d) => d.date === selectedDate) || null,
    [data, selectedDate]
  );

  /**
   * ✅ Convenciones esperadas desde la API (top-level por día):
   * - meta_usd, google_usd, total_usd, facturado_usd, neto_usd
   * - meta_conv, google_conv, total_conv
   * - meta_vtc, google_vtc, total_vtc (view-through conversions)
   * - meta_conv_total, google_conv_total, total_conv_total
   *
   * Y en el detalle por país:
   * - meta_usd, google_usd, total_usd, facturado_usd, neto_usd
   * - meta_conv, google_conv, total_conv
   * - meta_vtc, google_vtc, total_vtc
   * - meta_conv_total, google_conv_total, total_conv_total
   *
   * Si alguna no viene, el dashboard la trata como 0.
   */

  // ---- KPIs globales sobre el rango filtrado ----
  const globalKpis = useMemo(() => {
    const initial = {
      meta: 0,
      google: 0,
      total: 0,
      facturado: 0,
      neto: 0,

      meta_conv: 0,
      google_conv: 0,
      total_conv: 0,

      meta_vtc: 0,
      google_vtc: 0,
      total_vtc: 0,

      meta_conv_total: 0,
      google_conv_total: 0,
      total_conv_total: 0,
    };

    const sum = data.reduce((acc, d) => {
      acc.meta += n(d.meta_usd);
      acc.google += n(d.google_usd);
      acc.total += n(d.total_usd);
      acc.facturado += n(d.facturado_usd);
      acc.neto += n(d.neto_usd);

      acc.meta_conv += n(d.meta_conv);
      acc.google_conv += n(d.google_conv);
      acc.total_conv += n(d.total_conv);

      acc.meta_vtc += n(d.meta_vtc);
      acc.google_vtc += n(d.google_vtc);
      acc.total_vtc += n(d.total_vtc);

      acc.meta_conv_total += n(d.meta_conv_total);
      acc.google_conv_total += n(d.google_conv_total);
      acc.total_conv_total += n(d.total_conv_total);

      return acc;
    }, initial);

    const roiTotal = sum.total > 0 ? (sum.neto / sum.total) * 100 : 0;

    const cpaTotal = sum.total_conv_total > 0 ? sum.total / sum.total_conv_total : 0;
    const cpaMeta = sum.meta_conv_total > 0 ? sum.meta / sum.meta_conv_total : 0;
    const cpaGoogle = sum.google_conv_total > 0 ? sum.google / sum.google_conv_total : 0;

    return {
      ...sum,
      roiTotal,
      cpaTotal,
      cpaMeta,
      cpaGoogle,
    };
  }, [data]);

  // ---- Ranking de países por neto en todo el rango ----
  const countryRanking = useMemo(() => {
    const map = {};

    // Calcular días del período
    let periodDays = 1;
    if (from && to) {
      const fromDate = new Date(from.replace(/-/g, '/'));
      const toDate = new Date(to.replace(/-/g, '/'));
      periodDays = Math.max(1, Math.round((toDate - fromDate) / 86400000) + 1);
    }

    data.forEach((d) => {
      if (!d.countries) return;

      d.countries.forEach((c) => {
        const code = c.country || "UNK";
        if (!map[code]) {
          map[code] = {
            neto: 0,
            facturado: 0,
            total: 0,
            country_name: c.country_name || code,

            conv_total: 0,
          };
        }
        map[code].neto += n(c.neto_usd);
        map[code].facturado += n(c.facturado_usd);
        map[code].total += n(c.total_usd);
        map[code].conv_total += n(c.total_conv_total);
      });
    });

    const rows = Object.entries(map).map(([countryCode, v]) => {
      const roi = v.total > 0 ? (v.neto / v.total) * 100 : 0;
      const cpa = v.conv_total > 0 ? v.total / v.conv_total : 0;
      const netoPerDay = v.neto / periodDays;

      return {
        countryCode,
        countryName: v.country_name || countryCode,
        neto: v.neto,
        netoPerDay,
        facturado: v.facturado,
        total: v.total,
        roi,
        conv_total: v.conv_total,
        cpa,
      };
    });

    // Mostrar todos los países que tuvieron ventas (facturado > 0) o inversión (total > 0)
    return rows
      .filter((r) => r.facturado > 0 || r.total > 0)
      .sort((a, b) => b.neto - a.neto);
  }, [data, from, to]);

  // ---- Detalle agregado de países para todo el rango (formato compatible con DayDetailTable) ----
  const rangeDetailCountries = useMemo(() => {
    const map = {};

    data.forEach((d) => {
      if (!d.countries) return;

      d.countries.forEach((c) => {
        const code = c.country || "UNK";
        if (!map[code]) {
          map[code] = {
            country: code,
            country_name: c.country_name || code,
            meta_usd: 0,
            google_usd: 0,
            total_usd: 0,
            facturado_usd: 0,
            neto_usd: 0,
            meta_conv: 0,
            google_conv: 0,
            total_conv: 0,
            meta_vtc: 0,
            google_vtc: 0,
            total_vtc: 0,
            meta_conv_total: 0,
            google_conv_total: 0,
            total_conv_total: 0,
          };
        }
        map[code].meta_usd += n(c.meta_usd);
        map[code].google_usd += n(c.google_usd);
        map[code].total_usd += n(c.total_usd);
        map[code].facturado_usd += n(c.facturado_usd);
        map[code].neto_usd += n(c.neto_usd);
        map[code].meta_conv += n(c.meta_conv);
        map[code].google_conv += n(c.google_conv);
        map[code].total_conv += n(c.total_conv);
        map[code].meta_vtc += n(c.meta_vtc);
        map[code].google_vtc += n(c.google_vtc);
        map[code].total_vtc += n(c.total_vtc);
        map[code].meta_conv_total += n(c.meta_conv_total);
        map[code].google_conv_total += n(c.google_conv_total);
        map[code].total_conv_total += n(c.total_conv_total);
      });
    });

    return Object.values(map).filter((c) => c.facturado_usd > 0 || c.total_usd > 0);
  }, [data]);

  // ---- Estilos rápidos (tema claro) ----
  const cardStyle = {
    padding: "8px 10px",
    borderRadius: 6,
    background: "#FFFFFF",
    color: "#111827",
    border: "1px solid #E5E7EB",
  };

  const cardLabel = {
    fontSize: 10,
    opacity: 0.7,
    marginBottom: 1,
  };

  const cardValue = {
    fontSize: 14,
    fontWeight: 600,
  };

  const sectionTitle = {
    fontSize: 14,
    fontWeight: 600,
    marginBottom: 4,
    color: "#111827",
  };

  const sectionSubTitle = {
    fontSize: 11,
    opacity: 0.7,
    marginBottom: 8,
    color: "#4B5563",
  };

  return (
    <div
      style={{
        padding: "12px",
        color: "#111827",
        background: "#F3F4F6",
        minHeight: "100%",
      }}
    >
      {/* Filtros de fecha - Compacto */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 6,
          alignItems: "center",
          marginBottom: 12,
          background: "#FFFFFF",
          borderRadius: 8,
          padding: "8px 12px",
          border: "1px solid #E5E7EB",
        }}
      >
        {/* Presets rápidos */}
        {DATE_PRESETS.map((preset) => (
          <button
            key={preset.key}
            onClick={() => handlePresetClick(preset.key)}
            disabled={loading}
            style={{
              padding: "5px 10px",
              borderRadius: 5,
              border: activePreset === preset.key ? "2px solid #3B82F6" : "1px solid #D1D5DB",
              background: activePreset === preset.key ? "#EFF6FF" : "#FFFFFF",
              color: activePreset === preset.key ? "#1D4ED8" : "#374151",
              fontWeight: activePreset === preset.key ? 600 : 500,
              fontSize: 11,
              cursor: "pointer",
            }}
          >
            {preset.label}
          </button>
        ))}

        {/* Separador y rango actual */}
        <span style={{ fontSize: 11, color: "#6B7280", marginLeft: 8 }}>
          📅 {from && to ? `${from} → ${to}` : "Todo"}
        </span>

        {/* Botón de refresh */}
        <button
          onClick={handleRefresh}
          disabled={loading}
          title="Actualizar datos"
          style={{
            padding: "5px 10px",
            borderRadius: 5,
            border: "1px solid #D1D5DB",
            background: "#FFFFFF",
            color: "#374151",
            fontSize: 11,
            cursor: loading ? "not-allowed" : "pointer",
            marginLeft: 4,
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          🔄 Actualizar
        </button>

        {loading && <span style={{ fontSize: 11, color: "#6B7280", marginLeft: 8 }}>⏳</span>}
        {error && <span style={{ fontSize: 11, color: "#B91C1C", marginLeft: 8 }}>❌ {error}</span>}
      </div>

      {/* Selector de rango personalizado - solo si custom activo */}
      {activePreset === "custom" && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            alignItems: "center",
            marginBottom: 12,
            background: "#FFFFFF",
            borderRadius: 8,
            padding: "8px 12px",
            border: "1px solid #E5E7EB",
          }}
        >
          <input
            type="date"
            value={from || ""}
            onChange={(e) => setFrom(e.target.value || undefined)}
            style={{
              background: "#FFFFFF",
              border: "1px solid #D1D5DB",
              borderRadius: 5,
              padding: "5px 8px",
              color: "#111827",
              fontSize: 12,
            }}
          />
          <span style={{ color: "#9CA3AF" }}>→</span>
          <input
            type="date"
            value={to || ""}
            onChange={(e) => setTo(e.target.value || undefined)}
            style={{
              background: "#FFFFFF",
              border: "1px solid #D1D5DB",
              borderRadius: 5,
              padding: "5px 8px",
              color: "#111827",
              fontSize: 12,
            }}
          />
          <button
            onClick={handleApplyCustomRange}
            disabled={loading || !from || !to}
            style={{
              padding: "5px 12px",
              borderRadius: 5,
              border: "none",
              background: (!from || !to) ? "#D1D5DB" : "#3B82F6",
              color: "#FFFFFF",
              fontWeight: 600,
              fontSize: 11,
              cursor: (!from || !to) ? "not-allowed" : "pointer",
            }}
          >
            Aplicar
          </button>
        </div>
      )}

      {/* KPIs globales - 2 filas compactas */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(6, 1fr)",
          gap: 6,
          marginBottom: 12,
        }}
        className="kpi-grid"
      >
        <div style={cardStyle}>
          <div style={cardLabel}>Inv. Meta</div>
          <div style={cardValue}>{fmtUsd(globalKpis.meta)}</div>
        </div>
        <div style={cardStyle}>
          <div style={cardLabel}>Inv. Google</div>
          <div style={cardValue}>{fmtUsd(globalKpis.google)}</div>
        </div>
        <div style={cardStyle}>
          <div style={cardLabel}>Inv. Total</div>
          <div style={cardValue}>{fmtUsd(globalKpis.total)}</div>
        </div>
        <div style={cardStyle}>
          <div style={cardLabel}>Facturado</div>
          <div style={cardValue}>{fmtUsd(globalKpis.facturado)}</div>
        </div>
        <div style={{ ...cardStyle, background: globalKpis.neto >= 0 ? "#DCFCE7" : "#FEE2E2" }}>
          <div style={cardLabel}>Neto</div>
          <div style={cardValue}>{fmtUsd(globalKpis.neto)}</div>
        </div>
        <div style={cardStyle}>
          <div style={cardLabel}>ROI</div>
          <div style={cardValue}>{fmtNum(globalKpis.roiTotal)}%</div>
        </div>
      </div>

      {/* KPIs conversiones - segunda fila */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(6, 1fr)",
          gap: 6,
          marginBottom: 12,
        }}
        className="kpi-grid"
      >
        <div style={cardStyle}>
          <div style={cardLabel}>Conv. Total</div>
          <div style={cardValue}>{fmtNum(globalKpis.total_conv_total)}</div>
        </div>
        <div style={cardStyle}>
          <div style={cardLabel}>Conv. Meta</div>
          <div style={cardValue}>{fmtNum(globalKpis.meta_conv_total)}</div>
        </div>
        <div style={cardStyle}>
          <div style={cardLabel}>Conv. Google</div>
          <div style={cardValue}>{fmtNum(globalKpis.google_conv_total)}</div>
        </div>
        <div style={cardStyle}>
          <div style={cardLabel}>CPA Total</div>
          <div style={cardValue}>{fmtUsd(globalKpis.cpaTotal)}</div>
        </div>
        <div style={cardStyle}>
          <div style={cardLabel}>CPA Meta</div>
          <div style={cardValue}>{fmtUsd(globalKpis.cpaMeta)}</div>
        </div>
        <div style={cardStyle}>
          <div style={cardLabel}>CPA Google</div>
          <div style={cardValue}>{fmtUsd(globalKpis.cpaGoogle)}</div>
        </div>
      </div>

      {/* Layout principal: gráfico + ranking países + timeline ventas - responsive */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 280px 320px",
          gap: 12,
          alignItems: "stretch",
        }}
        className="main-grid"
      >
        {/* --- Gráfico diario --- */}
        <div
          style={{
            background: "#FFFFFF",
            borderRadius: 12,
            padding: 12,
            border: "1px solid #E5E7EB",
            boxShadow: "0 1px 2px rgba(15, 23, 42, 0.06)",
            minHeight: 350,
          }}
        >
          <div style={{ ...sectionTitle, marginBottom: 4 }}>Rendimiento diario</div>
          <div style={{ ...sectionSubTitle, marginBottom: 8 }}>
            Click en una fecha para ver detalle.
          </div>

          <div style={{ width: "100%", height: 300 }}>
            <ResponsiveContainer>
              <ComposedChart
                data={data}
                onClick={(e) => {
                  if (e && e.activeLabel) {
                    handlePointClick(e.activeLabel);
                  }
                }}
              >
                <CartesianGrid stroke="#E5E7EB" strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#6B7280" }} />

                {/* Eje USD */}
                <YAxis
                  yAxisId="usd"
                  tick={{ fontSize: 11, fill: "#6B7280" }}
                  tickFormatter={(v) => `$${v}`}
                />

                {/* Eje conversiones */}
                <YAxis
                  yAxisId="conv"
                  orientation="right"
                  tick={{ fontSize: 11, fill: "#6B7280" }}
                  tickFormatter={(v) => `${Math.round(v)}`}
                />

                <Tooltip
                  formatter={(value, name) => {
                    // Heurística: keys que terminan en _usd => $; keys que incluyen conv => num
                    if (typeof value === "number") {
                      if (String(name).includes("Conv") || String(name).includes("conv")) {
                        return [fmtNum(value), name];
                      }
                      return [fmtUsd(value), name];
                    }
                    return [value, name];
                  }}
                />

                <Legend />

                {/* Barras de inversión (stacked) */}
                <Bar
                  yAxisId="usd"
                  dataKey="meta_usd"
                  name="Meta USD"
                  stackId="spend"
                  fill="#3B82F6"
                  radius={[4, 4, 0, 0]}
                />
                <Bar
                  yAxisId="usd"
                  dataKey="google_usd"
                  name="Google USD"
                  stackId="spend"
                  fill="#F97316"
                  radius={[4, 4, 0, 0]}
                />

                {/* Facturado */}
                <Bar
                  yAxisId="usd"
                  dataKey="facturado_usd"
                  name="Facturado USD"
                  fill="#10B981"
                  radius={[4, 4, 0, 0]}
                />

                {/* Neto */}
                <Line
                  yAxisId="usd"
                  type="monotone"
                  dataKey="neto_usd"
                  name="Neto USD"
                  stroke="#FACC15"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />

                {/* Conversiones totales (línea en eje derecho) */}
                <Line
                  yAxisId="conv"
                  type="monotone"
                  dataKey="total_conv_total"
                  name="Conv total"
                  stroke="#8B5CF6"
                  strokeWidth={2}
                  dot={{ r: 2 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* --- Ranking países --- */}
        <div
          style={{
            background: "#FFFFFF",
            borderRadius: 12,
            padding: 12,
            border: "1px solid #E5E7EB",
            boxShadow: "0 1px 2px rgba(15, 23, 42, 0.06)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div style={{ ...sectionTitle, marginBottom: 4 }}>Países por neto</div>
          <div style={{ ...sectionSubTitle, marginBottom: 8 }}>
            Países con ventas o inversión.
          </div>

          <div style={{ flex: 1, maxHeight: isMobile ? 400 : 280, overflowY: "auto" }}>
            {/* Vista Mobile: Cards */}
            {isMobile ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {countryRanking.map((row) => (
                  <div
                    key={row.countryCode}
                    style={{
                      padding: "10px 12px",
                      borderRadius: 8,
                      background: "#F9FAFB",
                      border: "1px solid #E5E7EB",
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                      <span style={{ fontWeight: 600, fontSize: 13 }}>{row.countryName}</span>
                      <span style={{ fontWeight: 600, fontSize: 14, color: row.neto >= 0 ? "#059669" : "#DC2626" }}>
                        {fmtUsd(row.neto)}
                      </span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#6B7280" }}>
                      <span>Neto/Día: <span style={{ color: row.netoPerDay >= 0 ? "#059669" : "#DC2626" }}>{fmtUsd(row.netoPerDay)}</span></span>
                      <span>ROI: {fmtNum(row.roi)}%</span>
                      <span>CPA: {row.conv_total > 0 ? fmtUsd(row.cpa) : "—"}</span>
                    </div>
                  </div>
                ))}
                {countryRanking.length === 0 && (
                  <div style={{ padding: 16, textAlign: "center", color: "#6B7280" }}>Sin datos.</div>
                )}
              </div>
            ) : (
              /* Vista Desktop: Tabla */
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                <thead style={{ position: "sticky", top: 0, background: "#FFFFFF" }}>
                  <tr>
                    <th style={{ textAlign: "left", padding: "3px 4px", borderBottom: "1px solid #E5E7EB", color: "#4B5563", fontWeight: 500 }}>País</th>
                    <th style={{ textAlign: "right", padding: "3px 4px", borderBottom: "1px solid #E5E7EB", color: "#4B5563", fontWeight: 500 }}>Neto</th>
                    <th style={{ textAlign: "right", padding: "3px 4px", borderBottom: "1px solid #E5E7EB", color: "#4B5563", fontWeight: 500 }}>Neto/Día</th>
                    <th style={{ textAlign: "right", padding: "3px 4px", borderBottom: "1px solid #E5E7EB", color: "#4B5563", fontWeight: 500 }}>ROI</th>
                    <th style={{ textAlign: "right", padding: "3px 4px", borderBottom: "1px solid #E5E7EB", color: "#4B5563", fontWeight: 500 }}>CPA</th>
                  </tr>
                </thead>
                <tbody>
                  {countryRanking.map((row) => (
                    <tr key={row.countryCode}>
                      <td style={{ padding: "3px 4px", borderBottom: "1px solid #F3F4F6" }}>{row.countryName}</td>
                      <td style={{ padding: "3px 4px", textAlign: "right", borderBottom: "1px solid #F3F4F6", fontWeight: 500, color: row.neto >= 0 ? "#059669" : "#DC2626" }}>{fmtUsd(row.neto)}</td>
                      <td style={{ padding: "3px 4px", textAlign: "right", borderBottom: "1px solid #F3F4F6", color: row.netoPerDay >= 0 ? "#059669" : "#DC2626" }}>{fmtUsd(row.netoPerDay)}</td>
                      <td style={{ padding: "3px 4px", textAlign: "right", borderBottom: "1px solid #F3F4F6" }}>{fmtNum(row.roi)}%</td>
                      <td style={{ padding: "3px 4px", textAlign: "right", borderBottom: "1px solid #F3F4F6" }}>{row.conv_total > 0 ? fmtUsd(row.cpa) : "—"}</td>
                    </tr>
                  ))}
                  {countryRanking.length === 0 && (
                    <tr><td colSpan={5} style={{ padding: "6px", textAlign: "center", color: "#6B7280" }}>Sin datos.</td></tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* --- Timeline de ventas --- */}
        <SalesTimeline key={refreshKey} from={from} to={to} />
      </div>

      {/* ---- Detalle por país (día seleccionado o rango completo) ---- */}
      <div
        style={{
          marginTop: 16,
          background: "#FFFFFF",
          borderRadius: 12,
          padding: 12,
          border: "1px solid #E5E7EB",
          boxShadow: "0 1px 2px rgba(15, 23, 42, 0.06)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <div style={sectionTitle}>
            {selectedDayData
              ? `Detalle – ${selectedDayData.date}`
              : `Detalle del período ${from && to ? `(${from} → ${to})` : ""}`}
          </div>
          {selectedDayData && (
            <button
              onClick={() => setSelectedDate(null)}
              style={{
                padding: "4px 10px",
                borderRadius: 5,
                border: "1px solid #D1D5DB",
                background: "#F3F4F6",
                color: "#374151",
                fontSize: 11,
                cursor: "pointer",
              }}
            >
              ✕ Ver rango completo
            </button>
          )}
        </div>
        <div style={{ ...sectionSubTitle, marginBottom: 8 }}>
          {selectedDayData
            ? "Click en '✕ Ver rango completo' para volver al período."
            : "Click en una fecha del gráfico para ver detalle de ese día."}
          {" | "}Verde: margen &gt;20% | Amarillo: 0-20% | Rojo: negativo
        </div>

        <DayDetailTable
          countries={selectedDayData ? selectedDayData.countries || [] : rangeDetailCountries}
          isMobile={isMobile}
        />
      </div>

      {/* CSS para responsive */}
      <style>{`
        @media (max-width: 1200px) {
          .main-grid {
            grid-template-columns: 1fr 280px !important;
          }
          .main-grid > div:nth-child(3) {
            grid-column: 1 / -1;
          }
        }
        @media (max-width: 900px) {
          .main-grid {
            grid-template-columns: 1fr !important;
          }
          .main-grid > div:nth-child(3) {
            grid-column: auto;
          }
          .kpi-grid {
            grid-template-columns: repeat(3, 1fr) !important;
          }
        }
        @media (max-width: 600px) {
          .kpi-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
      `}</style>
    </div>
  );
};

// ---- Tabla detalle día por país ----
const DayDetailTable = ({ countries, isMobile }) => {
  const [expandedCard, setExpandedCard] = useState(null);

  const getBgColor = (row) => {
    if (n(row.facturado_usd) <= 0) {
      return n(row.total_usd) > 0 ? "#FEE2E2" : "transparent";
    }
    const margin = n(row.neto_usd) / n(row.facturado_usd);
    if (margin < 0) return "#FEE2E2";
    if (margin < 0.2) return "#FEF3C7";
    return "#DCFCE7";
  };

  const sortedCountries = [...countries].sort(
    (a, b) => n(b.total_usd) - n(a.total_usd)
  );

  // Calcula ventas estimadas por plataforma basado en proporción de conversiones
  const calcSalesByPlatform = (c) => {
    const fact = n(c.facturado_usd);
    const metaConv = n(c.meta_conv_total);
    const googleConv = n(c.google_conv_total);
    const totalConv = metaConv + googleConv;
    
    if (totalConv === 0) {
      return { metaSales: 0, googleSales: 0 };
    }
    
    const metaSales = (metaConv / totalConv) * fact;
    const googleSales = (googleConv / totalConv) * fact;
    
    return { metaSales, googleSales };
  };

  // Vista Mobile: Cards expandibles
  if (isMobile) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 12, maxHeight: 450, overflowY: "auto" }}>
        {sortedCountries.map((c) => {
          const fact = n(c.facturado_usd);
          const total = n(c.total_usd);
          const neto = n(c.neto_usd);
          const marginPct = fact > 0 ? (neto / fact) * 100 : 0;
          const metaSpend = n(c.meta_usd);
          const googleSpend = n(c.google_usd);
          const metaConv = n(c.meta_conv_total);
          const googleConv = n(c.google_conv_total);
          const totalConv = n(c.total_conv_total);
          const cpa = totalConv > 0 ? total / totalConv : 0;
          const { metaSales, googleSales } = calcSalesByPlatform(c);
          const metaNeto = metaSales - metaSpend;
          const googleNeto = googleSales - googleSpend;
          const isExpanded = expandedCard === c.country;

          return (
            <div
              key={c.country}
              onClick={() => setExpandedCard(isExpanded ? null : c.country)}
              style={{
                padding: "12px",
                borderRadius: 10,
                background: "#FFFFFF",
                border: `2px solid ${neto >= 0 ? "#10B981" : "#EF4444"}`,
                boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
            >
              {/* Header con país y neto */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontWeight: 700, fontSize: 14 }}>{c.country_name || c.country}</span>
                  <span style={{ 
                    fontSize: 10, 
                    color: "#6B7280",
                    transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 0.2s ease",
                  }}>▼</span>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontWeight: 700, fontSize: 16, color: neto >= 0 ? "#059669" : "#DC2626" }}>
                    {fmtUsd(neto)}
                  </div>
                  <div style={{ fontSize: 10, color: "#6B7280" }}>{fmtNum(marginPct)}% margen</div>
                </div>
              </div>

              {/* Resumen compacto (siempre visible) */}
              {!isExpanded && (
                <div style={{ 
                  display: "flex", 
                  gap: 12, 
                  marginTop: 8, 
                  fontSize: 11, 
                  color: "#6B7280" 
                }}>
                  <span>Inv: {fmtUsd(total)}</span>
                  <span>•</span>
                  <span>Ventas: {fmtUsd(fact)}</span>
                  <span>•</span>
                  <span>{fmtNum(totalConv)} conv</span>
                </div>
              )}

              {/* Detalles expandidos */}
              {isExpanded && (
                <div style={{ marginTop: 12 }}>
                  {/* Meta */}
                  <div style={{ 
                    padding: 10, 
                    background: "#EFF6FF", 
                    borderRadius: 8, 
                    marginBottom: 8,
                    borderLeft: "3px solid #3B82F6"
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                      <span style={{ fontWeight: 600, color: "#1E40AF", fontSize: 12 }}>Meta</span>
                      <span style={{ 
                        fontWeight: 600, 
                        fontSize: 12, 
                        color: metaNeto >= 0 ? "#059669" : "#DC2626" 
                      }}>
                        Neto: {fmtUsd(metaNeto)}
                      </span>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, fontSize: 11 }}>
                      <div>
                        <div style={{ color: "#6B7280" }}>Inversión</div>
                        <div style={{ fontWeight: 600, color: "#DC2626" }}>{fmtUsd(metaSpend)}</div>
                      </div>
                      <div>
                        <div style={{ color: "#6B7280" }}>Ventas</div>
                        <div style={{ fontWeight: 600, color: "#059669" }}>{fmtUsd(metaSales)}</div>
                      </div>
                      <div>
                        <div style={{ color: "#6B7280" }}>Conv</div>
                        <div style={{ fontWeight: 600 }}>{fmtNum(metaConv)}</div>
                      </div>
                    </div>
                  </div>

                  {/* Google */}
                  <div style={{ 
                    padding: 10, 
                    background: "#FFF7ED", 
                    borderRadius: 8, 
                    marginBottom: 8,
                    borderLeft: "3px solid #F97316"
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                      <span style={{ fontWeight: 600, color: "#C2410C", fontSize: 12 }}>Google</span>
                      <span style={{ 
                        fontWeight: 600, 
                        fontSize: 12, 
                        color: googleNeto >= 0 ? "#059669" : "#DC2626" 
                      }}>
                        Neto: {fmtUsd(googleNeto)}
                      </span>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, fontSize: 11 }}>
                      <div>
                        <div style={{ color: "#6B7280" }}>Inversión</div>
                        <div style={{ fontWeight: 600, color: "#DC2626" }}>{fmtUsd(googleSpend)}</div>
                      </div>
                      <div>
                        <div style={{ color: "#6B7280" }}>Ventas</div>
                        <div style={{ fontWeight: 600, color: "#059669" }}>{fmtUsd(googleSales)}</div>
                      </div>
                      <div>
                        <div style={{ color: "#6B7280" }}>Conv</div>
                        <div style={{ fontWeight: 600 }}>{fmtNum(googleConv)}</div>
                      </div>
                    </div>
                  </div>

                  {/* Totales */}
                  <div style={{ 
                    display: "grid", 
                    gridTemplateColumns: "1fr 1fr 1fr 1fr", 
                    gap: 8, 
                    padding: "10px 0", 
                    borderTop: "1px solid #E5E7EB",
                    fontSize: 11
                  }}>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ color: "#6B7280" }}>Inversión</div>
                      <div style={{ fontWeight: 600, color: "#DC2626" }}>{fmtUsd(total)}</div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ color: "#6B7280" }}>Ventas</div>
                      <div style={{ fontWeight: 600, color: "#059669" }}>{fmtUsd(fact)}</div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ color: "#6B7280" }}>Conv</div>
                      <div style={{ fontWeight: 600 }}>{fmtNum(totalConv)}</div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ color: "#6B7280" }}>CPA</div>
                      <div style={{ fontWeight: 600 }}>{totalConv > 0 ? fmtUsd(cpa) : "—"}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {sortedCountries.length === 0 && (
          <div style={{ padding: 16, textAlign: "center", color: "#6B7280" }}>Sin datos.</div>
        )}
      </div>
    );
  }

  // Vista Desktop: Tabla mejorada con colores por plataforma
  const thStyle = { 
    textAlign: "right", 
    padding: "6px 8px", 
    borderBottom: "2px solid #E5E7EB", 
    fontWeight: 600, 
    color: "#374151", 
    whiteSpace: "nowrap",
    fontSize: 10,
  };
  const tdStyle = { 
    textAlign: "right", 
    padding: "8px 8px", 
    borderBottom: "1px solid #F3F4F6", 
    whiteSpace: "nowrap",
    fontSize: 11,
  };

  return (
    <div style={{ overflowX: "auto", maxHeight: 400 }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead style={{ position: "sticky", top: 0, background: "#F9FAFB", zIndex: 1 }}>
          <tr>
            <th style={{ ...thStyle, textAlign: "left" }}>País</th>
            {/* Meta */}
            <th style={{ ...thStyle, background: "#EFF6FF", color: "#1E40AF", borderRadius: "6px 0 0 0" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 3 }}>
                <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#3B82F6" }} />
                Inv
              </div>
            </th>
            <th style={{ ...thStyle, background: "#EFF6FF", color: "#059669" }}>Vtas</th>
            <th style={{ ...thStyle, background: "#EFF6FF", color: "#1E40AF" }}>Conv</th>
            <th style={{ ...thStyle, background: "#EFF6FF", color: "#1E40AF", borderRadius: "0 6px 0 0" }}>Neto</th>
            {/* Google */}
            <th style={{ ...thStyle, background: "#FFF7ED", color: "#C2410C", borderRadius: "6px 0 0 0" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 3 }}>
                <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#F97316" }} />
                Inv
              </div>
            </th>
            <th style={{ ...thStyle, background: "#FFF7ED", color: "#059669" }}>Vtas</th>
            <th style={{ ...thStyle, background: "#FFF7ED", color: "#C2410C" }}>Conv</th>
            <th style={{ ...thStyle, background: "#FFF7ED", color: "#C2410C", borderRadius: "0 6px 0 0" }}>Neto</th>
            {/* Totales */}
            <th style={{ ...thStyle, background: "#F3F4F6" }}>Tot Inv</th>
            <th style={{ ...thStyle, background: "#DCFCE7", color: "#166534" }}>Tot Vtas</th>
            <th style={{ ...thStyle, fontWeight: 700 }}>Neto</th>
            <th style={thStyle}>%</th>
          </tr>
        </thead>
        <tbody>
          {sortedCountries.map((c, idx) => {
            const fact = n(c.facturado_usd);
            const total = n(c.total_usd);
            const neto = n(c.neto_usd);
            const marginPct = fact > 0 ? (neto / fact) * 100 : 0;
            const metaSpend = n(c.meta_usd);
            const googleSpend = n(c.google_usd);
            const metaConv = n(c.meta_conv_total);
            const googleConv = n(c.google_conv_total);
            const totalConv = n(c.total_conv_total);
            const { metaSales, googleSales } = calcSalesByPlatform(c);
            const metaNeto = metaSales - metaSpend;
            const googleNeto = googleSales - googleSpend;

            const rowBg = idx % 2 === 0 ? "#FFFFFF" : "#FAFAFA";

            return (
              <tr key={c.country} style={{ background: rowBg }}>
                <td style={{ ...tdStyle, textAlign: "left", fontWeight: 600, color: "#111827" }}>
                  {c.country_name || c.country}
                </td>
                {/* Meta */}
                <td style={{ ...tdStyle, background: "#EFF6FF", color: "#DC2626" }}>{fmtUsd(metaSpend)}</td>
                <td style={{ ...tdStyle, background: "#EFF6FF", color: "#059669" }}>{fmtUsd(metaSales)}</td>
                <td style={{ ...tdStyle, background: "#EFF6FF", fontWeight: 500 }}>{fmtNum(metaConv)}</td>
                <td style={{ ...tdStyle, background: "#EFF6FF", fontWeight: 600, color: metaNeto >= 0 ? "#059669" : "#DC2626" }}>
                  {fmtUsd(metaNeto)}
                </td>
                {/* Google */}
                <td style={{ ...tdStyle, background: "#FFF7ED", color: "#DC2626" }}>{fmtUsd(googleSpend)}</td>
                <td style={{ ...tdStyle, background: "#FFF7ED", color: "#059669" }}>{fmtUsd(googleSales)}</td>
                <td style={{ ...tdStyle, background: "#FFF7ED", fontWeight: 500 }}>{fmtNum(googleConv)}</td>
                <td style={{ ...tdStyle, background: "#FFF7ED", fontWeight: 600, color: googleNeto >= 0 ? "#059669" : "#DC2626" }}>
                  {fmtUsd(googleNeto)}
                </td>
                {/* Totales */}
                <td style={{ ...tdStyle, color: "#DC2626", fontWeight: 500 }}>{fmtUsd(total)}</td>
                <td style={{ ...tdStyle, background: "#DCFCE7", fontWeight: 600, color: "#166534" }}>{fmtUsd(fact)}</td>
                <td style={{ 
                  ...tdStyle, 
                  fontWeight: 700, 
                  color: neto >= 0 ? "#059669" : "#DC2626",
                  background: getBgColor(c),
                }}>
                  {fmtUsd(neto)}
                </td>
                <td style={{ 
                  ...tdStyle, 
                  fontWeight: 500,
                  color: marginPct >= 20 ? "#059669" : marginPct >= 0 ? "#D97706" : "#DC2626",
                }}>
                  {fmtNum(marginPct)}%
                </td>
              </tr>
            );
          })}

          {sortedCountries.length === 0 && (
            <tr>
              <td colSpan={13} style={{ ...tdStyle, textAlign: "center", color: "#6B7280", padding: 24 }}>
                Sin datos para este período.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

// ---- Timeline de ventas con infinite scroll ----
const SalesTimeline = ({ from, to }) => {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);
  const listRef = React.useRef(null);

  const fetchSales = async (offset = 0, append = false) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (from) params.append('from', from);
      if (to) params.append('to', to);
      params.append('offset', offset);
      params.append('limit', 10);

      const url = `${API_URI}/dashboard/sales-timeline?${params.toString()}`;
      const res = await fetch(url);
      
      if (!res.ok) throw new Error('Error al cargar ventas');
      
      const json = await res.json();
      
      if (append) {
        setSales(prev => [...prev, ...json.sales]);
        // Scroll al final después de cargar más
        setTimeout(() => {
          if (listRef.current) {
            listRef.current.scrollTo({
              top: listRef.current.scrollHeight,
              behavior: 'smooth'
            });
          }
        }, 100);
      } else {
        setSales(json.sales);
      }
      setHasMore(json.hasMore);
      setTotal(json.total);
    } catch (err) {
      console.error('Error fetching sales:', err);
    } finally {
      setLoading(false);
    }
  };

  // Recargar cuando cambian las fechas
  React.useEffect(() => {
    setSales([]);
    setHasMore(true);
    fetchSales(0, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to]);

  // Cargar más
  const handleLoadMore = () => {
    if (!loading && hasMore) {
      fetchSales(sales.length, true);
    }
  };

  // Formatear duración en años
  const formatDuration = (days) => {
    if (!days) return '?';
    const years = days / 365;
    if (years >= 1) {
      return years === 1 ? '1 año' : `${Math.round(years)} años`;
    }
    const months = Math.round(days / 30);
    return months === 1 ? '1 mes' : `${months} meses`;
  };

  // Formatear fecha relativa
  const formatRelativeTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Ahora';
    if (diffMins < 60) return `Hace ${diffMins}m`;
    if (diffHours < 24) return `Hace ${diffHours}h`;
    if (diffDays < 7) return `Hace ${diffDays}d`;
    
    return date.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
  };

  // Obtener nombre completo
  const getFullName = (sale) => {
    const parts = [sale.name, sale.lastname].filter(Boolean).map(s => s.trim()).filter(s => s);
    return parts.join(' ') || sale.email || 'Sin nombre';
  };

  // Obtener iniciales del nombre
  const getInitials = (sale) => {
    const name = sale.name?.trim();
    const lastname = sale.lastname?.trim();
    
    if (name && lastname) {
      return (name[0] + lastname[0]).toUpperCase();
    }
    if (name && name.length >= 2) {
      return name.substring(0, 2).toUpperCase();
    }
    if (sale.email) {
      return sale.email.substring(0, 2).toUpperCase();
    }
    return '??';
  };

  // Colores para avatares basado en el nombre
  const getAvatarColor = (name) => {
    const colors = [
      '#3B82F6', '#8B5CF6', '#EC4899', '#F97316', '#10B981',
      '#06B6D4', '#6366F1', '#EF4444', '#84CC16', '#F59E0B'
    ];
    const hash = (name || '').split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  return (
    <div
      style={{
        background: "#FFFFFF",
        borderRadius: 12,
        border: "1px solid #E5E7EB",
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        display: "flex",
        flexDirection: "column",
        height: 400,
        maxHeight: 400,
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "12px 16px",
          borderBottom: "1px solid #F3F4F6",
          display: "flex",
          flexShrink: 0,
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: "#111827" }}>
            💰 Ventas recientes
          </div>
          <div style={{ fontSize: 11, color: "#6B7280", marginTop: 2 }}>
            {loading && sales.length === 0
              ? 'Cargando ventas...'
              : total > 0
                ? `${total} ventas en el período`
                : 'Sin ventas en este período'}
          </div>
        </div>
        {loading && sales.length === 0 && (
          <div style={{ fontSize: 11, color: "#6B7280" }}>⏳</div>
        )}
      </div>

      {/* Sales list */}
      <div
        ref={listRef}
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          padding: "8px 0",
        }}
      >
        {sales.map((sale, idx) => (
          <div
            key={sale.id || idx}
            style={{
              display: "flex",
              alignItems: "center",
              padding: "10px 16px",
              borderBottom: idx < sales.length - 1 ? "1px solid #F9FAFB" : "none",
              transition: "background 0.15s",
              cursor: "default",
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = "#F9FAFB"}
            onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
          >
            {/* Avatar */}
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                background: `linear-gradient(135deg, ${getAvatarColor(getFullName(sale))}, ${getAvatarColor(getFullName(sale))}dd)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#FFFFFF",
                fontWeight: 600,
                fontSize: 13,
                marginRight: 12,
                flexShrink: 0,
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
            >
              {getInitials(sale)}
            </div>

            {/* Info */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 500,
                  color: "#111827",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {getFullName(sale)}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: "#6B7280",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  marginTop: 2,
                }}
              >
                <span style={{ 
                  background: "#F3F4F6", 
                  padding: "2px 6px", 
                  borderRadius: 4,
                  fontSize: 10,
                  fontWeight: 500,
                }}>
                  {sale.country || '??'}
                </span>
                <span style={{ 
                  background: "#EEF2FF", 
                  padding: "2px 6px", 
                  borderRadius: 4,
                  fontSize: 10,
                  fontWeight: 500,
                  color: "#4F46E5",
                }}>
                  {formatDuration(sale.duration_days)}
                </span>
              </div>
            </div>

            {/* Price & time */}
            <div style={{ textAlign: "right", flexShrink: 0, marginLeft: 8 }}>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: "#059669",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                }}
              >
                <span style={{ fontSize: 11, marginRight: 2 }}>$</span>
                {n(sale.price_usd).toFixed(2)}
              </div>
              <div style={{ fontSize: 10, color: "#9CA3AF", marginTop: 2 }}>
                {formatRelativeTime(sale.created_at)}
              </div>
            </div>
          </div>
        ))}

        {/* Loading indicator */}
        {loading && (
          <div style={{ padding: 16, textAlign: "center", color: "#6B7280", fontSize: 12 }}>
            Cargando...
          </div>
        )}

        {/* Empty state */}
        {!loading && sales.length === 0 && (
          <div style={{ padding: 32, textAlign: "center", color: "#9CA3AF" }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>🛒</div>
            <div style={{ fontSize: 13 }}>No hay ventas en este período</div>
          </div>
        )}

        {/* Ver más button */}
        {!loading && hasMore && sales.length > 0 && (
          <div style={{ padding: 12, textAlign: "center" }}>
            <button
              onClick={handleLoadMore}
              style={{
                padding: "8px 24px",
                borderRadius: 6,
                border: "none",
                background: "linear-gradient(135deg, #3B82F6, #2563EB)",
                color: "#FFFFFF",
                fontWeight: 600,
                fontSize: 12,
                cursor: "pointer",
                boxShadow: "0 2px 4px rgba(59, 130, 246, 0.3)",
                transition: "transform 0.15s, box-shadow 0.15s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.boxShadow = "0 4px 8px rgba(59, 130, 246, 0.4)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 2px 4px rgba(59, 130, 246, 0.3)";
              }}
            >
              Ver más
            </button>
          </div>
        )}

        {/* End of list */}
        {!loading && !hasMore && sales.length > 0 && (
          <div style={{ padding: 12, textAlign: "center", color: "#9CA3AF", fontSize: 11 }}>
            — Fin de la lista —
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardAdSpendPage;
